import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { warehouseService } from '_services/warehouse.service';
import Warehouse3DManager from './Warehouse3DManager';

// 1. Add API CRUD functions at the top (after imports):
const API_BASE = 'https://wms.mutu.solutions/api/warehouse';

// Fetch warehouse data (Read)
async function fetchWarehouseData(wmsId, rackId, binId) {
  const url = `${API_BASE}?wmsId=${wmsId}&rackId=${rackId}&binId=${binId}`;
  const res = await fetch(url);
  return res.json();
}
// Add (Create) a new bin/rack (example for bin)
async function createBin(wmsId, rackId, binData) {
  const url = `${API_BASE}/bin`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ wmsId, rackId, ...binData })
  });
  return res.json();
}
// Update a bin/rack (example for bin)
async function updateBin(binId, binData) {
  const url = `${API_BASE}/bin/${binId}`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(binData)
  });
  return res.json();
}
// Delete a bin/rack (example for bin)
async function deleteBin(binId) {
  const url = `${API_BASE}/bin/${binId}`;
  const res = await fetch(url, { method: 'DELETE' });
  return res.json();
}

const STATUS_COLORS = {
  damage: '#d32f2f',
  verification: '#ffeb3b',
  complete: '#388e3c',
  mt: '#90a4ae',
};

const STATUS_LIST = ['damage', 'verification', 'complete', 'mt'];

function getRandomStatus() {
  return STATUS_LIST[Math.floor(Math.random() * STATUS_LIST.length)];
}



// Layout variables (move these to top-level scope)
const areaSpacing = 30;
const shelfSpacing = 12;
const rowSpacing = 5;
const binSpacing = 4.5;
const binWidth = 2;
const binDepth = 2;
const binHeight = 1;
const shelfHeight = 0.5;
const shelfDepth = binDepth + 1;

const Store3DView = () => {
  // Add CSS animations
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0% { opacity: 0.6; }
        100% { opacity: 1; }
      }
      @keyframes slideInLeft {
        from { transform: translateX(-100px); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes slideInRight {
        from { transform: translateX(100px); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes slideOutRight {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(100px); opacity: 0; }
      }
      @keyframes fadeInUp {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      @keyframes glow {
        0% { box-shadow: 0 0 5px rgba(255,224,102,0.5); }
        50% { box-shadow: 0 0 20px rgba(255,224,102,0.8), 0 0 30px rgba(255,224,102,0.6); }
        100% { box-shadow: 0 0 5px rgba(255,224,102,0.5); }
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .warehouse-3d-container:hover {
        transform: translateY(-2px);
        transition: transform 0.3s ease;
      }
      .warehouse-3d-card:hover {
        box-shadow: 0 25px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1);
        transition: box-shadow 0.3s ease;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const mountRef = useRef();
  const labelRef = useRef();
  const controlsRef = useRef();
  const [popup, setPopup] = useState(null); // { label, status, x, y }
  const [sceneReady, setSceneReady] = useState(false);
  const [foundBin, setFoundBin] = useState(null); // { areaIdx, shelfIdx, rowIdx, binIdx, position }
  const [statusFilters, setStatusFilters] = useState({ damage: true, verification: true, complete: true, mt: true });
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [warehouseDetails, setWarehouseDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [warehouseLoading, setWarehouseLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showPanels, setShowPanels] = useState(true);
  const [showWarehouseInfo, setShowWarehouseInfo] = useState(true);
  const [showStatistics, setShowStatistics] = useState(true);
  const [showPanelPopup, setShowPanelPopup] = useState(false);
  // Add state for 3D/2D view
  const [is3DView, setIs3DView] = useState(true);
  // Add state for fullscreen
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef();
  // Add state for selected rack and bin
  const [selectedRack, setSelectedRack] = useState(null);
  const [selectedBin, setSelectedBin] = useState(null);
  const [rackOptions, setRackOptions] = useState([]);
  const [binOptions, setBinOptions] = useState([]);

  // Close popup when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popup && !event.target.closest('.popup-content')) {
        console.log('Clicking outside popup');
        setPopup(null);
      }
    };

    const handleKeyDown = (event) => {
      if (popup && event.key === 'Escape') {
        console.log('Escape key pressed');
        setPopup(null);
      }
    };

    if (popup) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [popup]);

  // Function to center camera on a specific bin
  const centerOnBin = (binLabel) => {
    if (!controlsRef.current || !warehouseDetails) return;
    
    // Find the bin in the warehouse data
    let targetPosition = null;
    let currentZ = 0;
    
    warehouseDetails.racks.forEach((rack, rackIndex) => {
      rack.stockBins.forEach((bin, binIndex) => {
        if (bin.code === binLabel) {
          // Calculate position based on rack and bin index
          const x = -rack.stockBins.length * binSpacing / 2 + binIndex * binSpacing;
          const y = 0;
          const z = currentZ;
          targetPosition = new THREE.Vector3(x, y, z);
        }
      });
      currentZ += areaSpacing;
    });
    
    if (targetPosition && controlsRef.current) {
      const camera = controlsRef.current.object;
      camera.position.set(targetPosition.x, targetPosition.y + 20, targetPosition.z + 30);
      controlsRef.current.target.copy(targetPosition);
      controlsRef.current.update();
    }
  };




  // Determine bin status based on stock data
  const getBinStatus = (bin) => {
    const utilization = bin.availableStockQuantity / bin.allowedStockQuantity;
    if (utilization === 0) return 'mt'; // Empty
    if (utilization >= 0.8) return 'damage'; // High utilization
    if (utilization >= 0.5) return 'verification'; // Medium utilization
    return 'complete'; // Low utilization
  };

  // Get bin color based on stock data
  const getBinColor = (bin) => {
    const utilization = bin.availableStockQuantity / bin.allowedStockQuantity;
    if (utilization === 0) return '#90a4ae'; // Empty - Gray
    if (utilization >= 0.8) return '#d32f2f'; // High utilization - Red
    if (utilization >= 0.5) return '#ffeb3b'; // Medium utilization - Yellow
    return '#388e3c'; // Low utilization - Green
  };

  // Get bin height based on stock quantity
  const getBinHeight = (bin) => {
    const utilization = bin.availableStockQuantity / bin.allowedStockQuantity;
    return Math.max(0.5, utilization * binHeight); // Minimum height of 0.5, scales with utilization
  };

  // Convert real warehouse data to 3D visualization format - flattened structure without rows
  const convertWarehouseDataTo3D = (warehouseData) => {
    if (!warehouseData || !warehouseData.racks) return [];
    
    const allRacks = [];
    let rackCounter = 0;
    
    warehouseData.racks.forEach((rack, rackIndex) => {
      // Group bins by their actual rack code (extracted from bin code)
      const binsByRack = {};
      
      rack.stockBins.forEach((bin) => {
        // Extract rack code from bin code (e.g., "R04" from "R04", "R05M01" -> "R05")
        let rackCode = bin.code;
        if (bin.code.includes('M') || bin.code.includes('L')) {
          // For codes like "R05M01", extract "R05"
          rackCode = bin.code.substring(0, bin.code.indexOf('M') !== -1 ? bin.code.indexOf('M') : bin.code.indexOf('L'));
        }
        
        if (!binsByRack[rackCode]) {
          binsByRack[rackCode] = [];
        }
        binsByRack[rackCode].push(bin);
      });
      
      // Create separate racks for each group with unique positioning
      Object.entries(binsByRack).forEach(([rackCode, bins], groupIndex) => {
        allRacks.push({
        name: `Rack-${rackCode.replace('R', '')}`,
        code: rackCode,
        id: `${rack.id}_${groupIndex}`,
          rackIndex: rackCounter++, // Unique rack index for positioning
        bins: bins.map((bin, binIndex) => ({
          label: bin.code,
          status: getBinStatus(bin),
          binData: bin,
          color: getBinColor(bin),
            height: getBinHeight(bin),
            binIndex: binIndex // Unique bin index within rack
        }))
        });
      });
      
      // If no bins, return empty rack structure
      if (rack.stockBins.length === 0) {
        allRacks.push({
          name: rack.name,
          code: rack.code,
          id: rack.id,
          rackIndex: rackCounter++,
          bins: []
        });
      }
    });
      
    return allRacks;
  };

  // Use real warehouse data if available, otherwise fall back to mock data
  const warehouseAreas = warehouseDetails ? convertWarehouseDataTo3D(warehouseDetails) : [];



  // Load warehouses on component mount
  useEffect(() => {
        setWarehouseLoading(true);
    warehouseService.getSuggestion()
      .then(response => {
        if (response && response.content) {
          setWarehouses(response.content);
          // Optionally auto-select the first warehouse
          if (response.content.length > 0) {
            setSelectedWarehouse(response.content[0]);
          }
        }
      })
      .catch(() => setWarehouses([]))
      .finally(() => setWarehouseLoading(false));
  }, []);

  // 2. Fetch warehouse details when selectedWarehouse changes
  useEffect(() => {
    if (!selectedWarehouse) return;
    setWarehouseLoading(true);
    // Use the selected warehouse's id for wmsId, and fetch all racks/bins
    warehouseService.fetchWarehouseData(selectedWarehouse.id, '', '')
      .then(data => {
        if (data && data.content && data.content.length > 0) {
          // setWarehouseDetails(data.content[0]);
        }
      })
      .catch(() => setWarehouseDetails(null))
      .finally(() => setWarehouseLoading(false));
  }, [selectedWarehouse]);

  // Update rack options when selectedWarehouse changes
  useEffect(() => {
    if (!selectedWarehouse) {
      setRackOptions([]);
      setSelectedRack(null);
      setBinOptions([]);
      setSelectedBin(null);
      return;
    }
    setWarehouseLoading(true);
    warehouseService.getRacksByWarehouseId(selectedWarehouse.id)
      .then(data => {
        if (data && data.content && data.content.racks) {
          setRackOptions(data.content.racks);
          setSelectedRack(null);
          setBinOptions([]);
          setSelectedBin(null);
        } else {
          setRackOptions([]);
          setSelectedRack(null);
          setBinOptions([]);
          setSelectedBin(null);
        }
      })
      .catch(() => {
        setRackOptions([]);
        setSelectedRack(null);
        setBinOptions([]);
        setSelectedBin(null);
      })
      .finally(() => setWarehouseLoading(false));
  }, [selectedWarehouse]);

  // Update bin options when selectedRack changes
  useEffect(() => {
    if (selectedRack && selectedRack.stockBins) {
      setBinOptions(selectedRack.stockBins);
      setSelectedBin(null);
    } else {
      setBinOptions([]);
      setSelectedBin(null);
    }
  }, [selectedRack]);

  // Center on bin when selectedBin changes
  useEffect(() => {
    if (selectedBin && selectedBin.code) {
      centerOnBin(selectedBin.code);
    }
  }, [selectedBin]);

  // Filter bins by status - only show racks that exist in real data
  const deeplyFilteredAreas = warehouseDetails ? warehouseAreas.map(area => ({
    ...area,
    bins: area.bins.filter(bin => statusFilters[bin.status])
  })).filter(area => area.bins.length > 0) : [];



  useEffect(() => {
    if (!is3DView) return; // <-- Only run when 3D view is active

    setSceneReady(false);
    setFoundBin(null);
    // Scene setup - Light theme like the reference
    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#e8f4fd');
    scene.fog = new THREE.Fog('#e8f4fd', 100, 200);
    const camera = new THREE.PerspectiveCamera(75, mountRef.current.clientWidth / mountRef.current.clientHeight, 0.1, 1000);
    camera.position.set(0, 40, 100);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    renderer.shadowMap.enabled = true;
    mountRef.current.appendChild(renderer.domElement);

    // CSS2DRenderer for bin and section labels
    const labelRenderer = new CSS2DRenderer();
    labelRenderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    labelRenderer.domElement.style.position = 'absolute';
    labelRenderer.domElement.style.top = '0px';
    labelRenderer.domElement.style.pointerEvents = 'none';
    labelRef.current.appendChild(labelRenderer.domElement);

    // Orbit controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.screenSpacePanning = false;
    controls.minDistance = 10;
    controls.maxDistance = 300;
    controls.maxPolarAngle = Math.PI / 2;
    controlsRef.current = controls;

    // Simple, clean lighting like the reference
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambientLight);
    
    // Main directional light for shadows
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.5);
    dirLight.position.set(30, 60, 40);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.width = 1024;
    dirLight.shadow.mapSize.height = 1024;
    dirLight.shadow.camera.near = 0.5;
    dirLight.shadow.camera.far = 500;
    dirLight.shadow.camera.left = -100;
    dirLight.shadow.camera.right = 100;
    dirLight.shadow.camera.top = 100;
    dirLight.shadow.camera.bottom = -100;
    scene.add(dirLight);

    // Light blue floor like the reference
    const groundGeometry = new THREE.PlaneGeometry(300, 300);
    const groundMaterial = new THREE.MeshPhongMaterial({ 
      color: '#b8d4e3', 
      shininess: 5
    });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -2.5;
    ground.receiveShadow = true;
    scene.add(ground);

    // Grid pattern like the reference
    const gridHelper = new THREE.GridHelper(300, 60, '#8bb3c7', '#a8c4d3');
    gridHelper.position.y = -2.49;
    scene.add(gridHelper);

    // Light brown walls like the reference
    const wallMaterial = new THREE.MeshPhongMaterial({ 
      color: '#d4b895', 
      side: THREE.BackSide, 
      shininess: 5
    });
    
    // Back wall
    const backWall = new THREE.Mesh(new THREE.PlaneGeometry(300, 80), wallMaterial);
    backWall.position.set(0, 37, -150);
    scene.add(backWall);
    
    // Left wall
    const leftWall = new THREE.Mesh(new THREE.PlaneGeometry(300, 80), wallMaterial);
    leftWall.position.set(-150, 37, 0);
    leftWall.rotation.y = Math.PI / 2;
    scene.add(leftWall);
    
    // Right wall
    const rightWall = new THREE.Mesh(new THREE.PlaneGeometry(300, 80), wallMaterial);
    rightWall.position.set(150, 37, 0);
    rightWall.rotation.y = -Math.PI / 2;
    scene.add(rightWall);
    
    // Light grey ceiling like the reference
    const ceiling = new THREE.Mesh(
      new THREE.PlaneGeometry(300, 300), 
      new THREE.MeshPhongMaterial({ 
        color: '#c0c0c0', 
        side: THREE.BackSide, 
        shininess: 5
      })
    );
    ceiling.position.set(0, 77, 0);
    ceiling.rotation.x = Math.PI / 2;
    scene.add(ceiling);

    // Simple pillars like the reference
    const pillarMaterial = new THREE.MeshPhongMaterial({ 
      color: '#a0a0a0', 
      shininess: 5
    });
    const pillarGeometry = new THREE.CylinderGeometry(1, 1, 80, 8);
    const pillarPositions = [
      [-120, 38, -120],
      [120, 38, -120],
      [-120, 38, 120],
      [120, 38, 120]
    ];
    pillarPositions.forEach(([x, y, z]) => {
      const pillar = new THREE.Mesh(pillarGeometry, pillarMaterial);
      pillar.position.set(x, y, z);
      pillar.castShadow = true;
      scene.add(pillar);
    });

    // Store bin meshes for raycasting
    const binMeshes = [];

    let z = 0;
    deeplyFilteredAreas.forEach((area, areaIdx) => {
      // Use unique rack index for positioning to prevent overlap
      const rackIndex = area.rackIndex || areaIdx;
      // Spread racks horizontally as well to prevent overlap
      let x = -area.bins.length * binSpacing / 2 + (rackIndex * 20);
      
      // Draw shelf base for the entire rack
      const shelfGeometry = new THREE.BoxGeometry(area.bins.length * binSpacing, shelfHeight, shelfDepth);
      const shelfMaterial = new THREE.MeshPhongMaterial({ color: '#444', transparent: true, opacity: 0.7 });
      const shelfMesh = new THREE.Mesh(shelfGeometry, shelfMaterial);
      shelfMesh.position.set(x + (area.bins.length * binSpacing) / 2 - binSpacing / 2, -binHeight / 2 - shelfHeight / 2, z);
      scene.add(shelfMesh);

      // --- Begin: Attach rack label to a group for the rack ---
      const rackGroup = new THREE.Group();
      rackGroup.position.set(x, 0, z);
      scene.add(rackGroup);

      // Rack label like the reference
      const rackLabel = document.createElement('div');
      rackLabel.className = 'rack-label';
      rackLabel.textContent = area.name;
      rackLabel.style.color = '#333';
      rackLabel.style.fontSize = '14px';
      rackLabel.style.fontWeight = 'bold';
      rackLabel.style.background = 'rgba(255,255,255,0.9)';
      rackLabel.style.padding = '4px 8px';
      rackLabel.style.borderRadius = '4px';
      rackLabel.style.whiteSpace = 'pre';
      rackLabel.style.textAlign = 'center';
      rackLabel.style.marginRight = '8px';
      rackLabel.style.border = '1px solid #ccc';
      const rackLabelObj = new CSS2DObject(rackLabel);
      rackLabelObj.position.set(0, 1, 0); // Fixed relative to rackGroup
      rackGroup.add(rackLabelObj);
      // --- End: Attach rack label to rack group ---

      // Draw bins as children of rackGroup
      for (let i = 0; i < area.bins.length; i++) {
        const bin = area.bins[i];
        const color = bin.color || STATUS_COLORS[bin.status];
        const binHeightValue = bin.height || binHeight;
        
        // Simple, clean material like the reference
        const material = new THREE.MeshPhongMaterial({
          color,
          shininess: 10,
          flatShading: true
        });
        
        // Create bin with dynamic height based on stock
        const box = new THREE.Mesh(new THREE.BoxGeometry(binWidth, binHeightValue, binDepth), material);
        box.position.set(i * binSpacing, binHeightValue / 2 - binHeight / 2, 0); // Position relative to rackGroup
        box.castShadow = true;
        box.receiveShadow = false;
        // Attach bin mesh to rackGroup
        rackGroup.add(box);
        
        // Simple highlight for bins with stock
        if (bin.binData && bin.binData.availableStockQuantity > 0) {
          const highlightGeometry = new THREE.BoxGeometry(binWidth + 0.1, binHeightValue + 0.1, binDepth + 0.1);
          const highlightMaterial = new THREE.MeshBasicMaterial({ 
            color: 0xffffff, 
            transparent: true, 
            opacity: 0.2 
          });
          const highlight = new THREE.Mesh(highlightGeometry, highlightMaterial);
          highlight.position.copy(box.position);
          scene.add(highlight);
        }
        
        // Highlight found bin or selected bin
        if ((foundBin && foundBin.areaIdx === areaIdx && foundBin.binIdx === i) || 
            (popup && popup.label === bin.label)) {
          box.material = new THREE.MeshPhysicalMaterial({ color: '#ffe066', metalness: 0.2, roughness: 0.1 });
          box.scale.set(1.2, 1.2, 1.2);
          
          // Add pulsing glow effect for selected bin
          const glowGeometry = new THREE.BoxGeometry(binWidth + 0.4, binHeightValue + 0.4, binDepth + 0.4);
          const glowMaterial = new THREE.MeshBasicMaterial({ 
            color: '#ffe066', 
            transparent: true, 
            opacity: 0.6 
          });
          const selectedGlow = new THREE.Mesh(glowGeometry, glowMaterial);
          selectedGlow.position.copy(box.position);
          scene.add(selectedGlow);
        }
        
        // Simple outline like the reference
        const edgeGeom = new THREE.EdgesGeometry(new THREE.BoxGeometry(binWidth, binHeightValue, binDepth));
        const edgeMat = new THREE.LineBasicMaterial({ color: '#666', linewidth: 1 });
        const edges = new THREE.LineSegments(edgeGeom, edgeMat);
        edges.position.copy(box.position);
        scene.add(box);
        scene.add(edges);
        
        // Attach bin data for raycasting
        box.userData = { 
          label: bin.label, 
          status: bin.status, 
          position: box.position.clone(),
          binData: bin.binData // Include real bin data if available
        };
        binMeshes.push(box);

        // Bin label like the reference
        const binLabel = document.createElement('div');
        binLabel.className = 'bin-label';
        binLabel.textContent = bin.label;
        binLabel.style.color = '#333';
        binLabel.style.fontSize = '9px';
        binLabel.style.fontWeight = 'bold';
        binLabel.style.background = 'rgba(255,255,255,0.8)';
        binLabel.style.padding = '1px 4px';
        binLabel.style.borderRadius = '2px';
        binLabel.style.whiteSpace = 'pre';
        binLabel.style.textAlign = 'center';
        binLabel.style.border = '1px solid #ccc';
        binLabel.style.pointerEvents = 'none';
        binLabel.style.zIndex = '1000';
        binLabel.style.position = 'relative';
        binLabel.style.minWidth = '40px';
        binLabel.style.display = 'inline-block';
        const binLabelObj = new CSS2DObject(binLabel);
        
        // Calculate unique position for each bin label to prevent overlap
        const labelX = x + i * binSpacing;
        const labelY = binHeightValue + 0.5;
        // Use unique bin index and rack index for better spacing
        const uniqueBinIndex = bin.binIndex || i;
        const uniqueRackIndex = area.rackIndex || areaIdx;
        // Create a much more aggressive staggered pattern to prevent overlap
        const labelZ = z + (uniqueBinIndex * 2.0) + (uniqueRackIndex * 1.0);
        
        // Temporarily disable all labels to test
        // if (bin.binData && bin.binData.availableStockQuantity > 0 || i % 7 === 0) {
        //   binLabelObj.position.set(labelX, labelY, labelZ);
        //   scene.add(binLabelObj);
        // }
      }
      
      // Add extra spacing between racks to prevent label overlap
      z += areaSpacing + (rackIndex * 20) + 30;
    });

    // Create a separate label system to prevent overlap
    let labelZ = 0;
    deeplyFilteredAreas.forEach((area, areaIdx) => {
      const rackIndex = area.rackIndex || areaIdx;
      let x = -area.bins.length * binSpacing / 2 + (rackIndex * 20);
      
      // Create labels for this rack in a separate layer
      area.bins.forEach((bin, i) => {
        // Only show labels for bins with stock or every 10th bin
        if (bin.binData && bin.binData.availableStockQuantity > 0 || i % 10 === 0) {
          const binLabel = document.createElement('div');
          binLabel.className = 'bin-label';
          binLabel.textContent = bin.label;
          binLabel.style.color = '#333';
          binLabel.style.fontSize = '9px';
          binLabel.style.fontWeight = 'bold';
          binLabel.style.background = 'rgba(255,255,255,0.8)';
          binLabel.style.padding = '1px 4px';
          binLabel.style.borderRadius = '2px';
          binLabel.style.whiteSpace = 'pre';
          binLabel.style.textAlign = 'center';
          binLabel.style.border = '1px solid #ccc';
          binLabel.style.pointerEvents = 'none';
          binLabel.style.zIndex = '1000';
          binLabel.style.position = 'relative';
          binLabel.style.minWidth = '40px';
          binLabel.style.display = 'inline-block';
          
          const binLabelObj = new CSS2DObject(binLabel);
          const labelX = x + i * binSpacing;
          const labelY = 2; // Fixed height above ground
          const labelZPos = labelZ + (i * 0.5); // Much more spacing
          
          binLabelObj.position.set(labelX, labelY, labelZPos);
        scene.add(binLabelObj);
      }
      });
      
      labelZ += 50; // Much more spacing between rack label layers
    });

    // Raycaster for bin click
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    function onClick(event) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(binMeshes);
      if (intersects.length > 0) {
        const bin = intersects[0].object.userData;
        setPopup({
          label: bin.label,
          status: bin.status,
          x: event.clientX,
          y: event.clientY,
          binData: bin.binData
        });
      } else {
        setPopup(null);
      }
    }
    renderer.domElement.addEventListener('click', onClick);

    // Simple pathway lines like the reference
    const pathwayMaterial = new THREE.LineBasicMaterial({ 
      color: '#666666', 
      linewidth: 1
    });
    const pathwayPoints = [
      new THREE.Vector3(-120, -2.48, 0),
      new THREE.Vector3(120, -2.48, 0)
    ];
    const pathwayGeometry = new THREE.BufferGeometry().setFromPoints(pathwayPoints);
    const pathwayLine = new THREE.Line(pathwayGeometry, pathwayMaterial);
    scene.add(pathwayLine);

    // Add warehouse information on the back wall
    if (selectedWarehouse) {
      const warehouseInfoDiv = document.createElement('div');
      warehouseInfoDiv.innerHTML = `
        <div style="text-align: center; line-height: 1.2;">
          <div style="font-size: 20px; font-weight: bold; color: #333; margin-bottom: 6px;">${selectedWarehouse.name}</div>
          <div style="font-size: 14px; color: #666; margin-bottom: 4px;">Capacity: ${selectedWarehouse.maxCapacities?.[0]?.volume || 0} ${selectedWarehouse.maxCapacities?.[0]?.measurement?.name || 'CBM'}</div>
          <div style="font-size: 12px; color: #999;">ID: ${selectedWarehouse.id}</div>
        </div>
      `;
      warehouseInfoDiv.style.background = 'rgba(255,255,255,0.95)';
      warehouseInfoDiv.style.padding = '12px 16px';
      warehouseInfoDiv.style.borderRadius = '6px';
      warehouseInfoDiv.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
      warehouseInfoDiv.style.border = '1px solid #ccc';
      warehouseInfoDiv.style.minWidth = '250px';
      const warehouseInfoLabel = new CSS2DObject(warehouseInfoDiv);
      warehouseInfoLabel.position.set(0, 45, -99.5);
      scene.add(warehouseInfoLabel);
    } else {
      // Fallback logo when no warehouse is selected
    const logoDiv = document.createElement('div');
    logoDiv.textContent = 'WAREHOUSE MANAGEMENT';
    logoDiv.style.fontSize = '24px';
    logoDiv.style.fontWeight = 'bold';
    logoDiv.style.color = '#333';
    logoDiv.style.background = 'rgba(255,255,255,0.95)';
    logoDiv.style.padding = '10px 20px';
    logoDiv.style.borderRadius = '6px';
    logoDiv.style.letterSpacing = '1px';
    logoDiv.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
    logoDiv.style.border = '1px solid #ccc';
    logoDiv.style.textAlign = 'center';
    const logoLabel = new CSS2DObject(logoDiv);
    logoLabel.position.set(0, 45, -99.5);
    scene.add(logoLabel);
    }

    // Add human workers
    const createWorker = (x, z, color = '#4a90e2') => {
      const workerGroup = new THREE.Group();
      
      // Body
      const bodyGeometry = new THREE.CylinderGeometry(0.3, 0.4, 1.2, 8);
      const bodyMaterial = new THREE.MeshPhongMaterial({ color });
      const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
      body.position.y = 0.6;
      workerGroup.add(body);
      
      // Head
      const headGeometry = new THREE.SphereGeometry(0.25, 8, 8);
      const headMaterial = new THREE.MeshPhongMaterial({ color: '#f4d03f' });
      const head = new THREE.Mesh(headGeometry, headMaterial);
      head.position.y = 1.5;
      workerGroup.add(head);
      
      // Arms
      const armGeometry = new THREE.CylinderGeometry(0.08, 0.08, 0.8, 6);
      const armMaterial = new THREE.MeshPhongMaterial({ color: '#f4d03f' });
      const leftArm = new THREE.Mesh(armGeometry, armMaterial);
      leftArm.position.set(-0.4, 0.8, 0);
      leftArm.rotation.z = Math.PI / 6;
      workerGroup.add(leftArm);
      
      const rightArm = new THREE.Mesh(armGeometry, armMaterial);
      rightArm.position.set(0.4, 0.8, 0);
      rightArm.rotation.z = -Math.PI / 6;
      workerGroup.add(rightArm);
      
      // Legs
      const legGeometry = new THREE.CylinderGeometry(0.1, 0.1, 0.8, 6);
      const legMaterial = new THREE.MeshPhongMaterial({ color: '#34495e' });
      const leftLeg = new THREE.Mesh(legGeometry, legMaterial);
      leftLeg.position.set(-0.15, -0.2, 0);
      workerGroup.add(leftLeg);
      
      const rightLeg = new THREE.Mesh(legGeometry, legMaterial);
      rightLeg.position.set(0.15, -0.2, 0);
      workerGroup.add(rightLeg);
      
      // Safety vest
      const vestGeometry = new THREE.CylinderGeometry(0.35, 0.45, 0.8, 8);
      const vestMaterial = new THREE.MeshPhongMaterial({ color: '#e74c3c' });
      const vest = new THREE.Mesh(vestGeometry, vestMaterial);
      vest.position.y = 0.6;
      workerGroup.add(vest);
      
      workerGroup.position.set(x, 0, z);
      workerGroup.userData = {
        type: 'worker',
        offset: Math.random() * Math.PI * 2,
        legs: [leftLeg, rightLeg],
        arms: [leftArm, rightArm]
      };
      
      return workerGroup;
    };
    
    // Add workers at different positions
    const workers = [
      createWorker(-20, -30, '#4a90e2'),
      createWorker(15, -25, '#9b59b6'),
      createWorker(-10, 20, '#e67e22'),
      createWorker(25, 15, '#27ae60')
    ];
    
    workers.forEach(worker => {
      scene.add(worker);
    });
    
    // Add lorries (trucks)
    const createLorry = (x, z, color = '#34495e') => {
      const lorryGroup = new THREE.Group();
      
      // Main body
      const bodyGeometry = new THREE.BoxGeometry(4, 2, 2);
      const bodyMaterial = new THREE.MeshPhongMaterial({ color });
      const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
      body.position.y = 1;
      lorryGroup.add(body);
      
      // Cabin
      const cabinGeometry = new THREE.BoxGeometry(1.5, 1.8, 2);
      const cabinMaterial = new THREE.MeshPhongMaterial({ color: '#2c3e50' });
      const cabin = new THREE.Mesh(cabinGeometry, cabinMaterial);
      cabin.position.set(-1.5, 1.4, 0);
      lorryGroup.add(cabin);
      
      // Wheels
      const wheelGeometry = new THREE.CylinderGeometry(0.4, 0.4, 0.3, 8);
      const wheelMaterial = new THREE.MeshPhongMaterial({ color: '#2c3e50' });
      const wheels = [];
      
      const wheelPositions = [
        [-1.2, 0.4, -1.1], [-1.2, 0.4, 1.1],  // Front wheels
        [1.2, 0.4, -1.1], [1.2, 0.4, 1.1]     // Back wheels
      ];
      
      wheelPositions.forEach(pos => {
        const wheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
        wheel.position.set(...pos);
        wheel.rotation.z = Math.PI / 2;
        lorryGroup.add(wheel);
        wheels.push(wheel);
      });
      
      // Loading area
      const loadingGeometry = new THREE.BoxGeometry(3.5, 0.1, 1.8);
      const loadingMaterial = new THREE.MeshPhongMaterial({ color: '#95a5a6' });
      const loading = new THREE.Mesh(loadingGeometry, loadingMaterial);
      loading.position.set(0.5, 2.1, 0);
      lorryGroup.add(loading);
      
      lorryGroup.position.set(x, 0, z);
      lorryGroup.position.y = -1; // Slightly below ground for wheels
      lorryGroup.userData = {
        type: 'lorry',
        speed: 0.05 + Math.random() * 0.03,
        wheels: wheels
      };
      
      return lorryGroup;
    };
    
    // Add lorries
    const lorries = [
      createLorry(-80, -10, '#34495e'),
      createLorry(60, 20, '#8e44ad'),
      createLorry(-40, 40, '#e67e22')
    ];
    
    lorries.forEach(lorry => {
      scene.add(lorry);
    });
    
    // Add forklifts
    const createForklift = (x, z, color = '#e74c3c') => {
      const forkliftGroup = new THREE.Group();
      
      // Main body
      const bodyGeometry = new THREE.BoxGeometry(1.5, 1, 1);
      const bodyMaterial = new THREE.MeshPhongMaterial({ color });
      const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
      body.position.y = 0.5;
      forkliftGroup.add(body);
      
      // Driver seat
      const seatGeometry = new THREE.BoxGeometry(0.8, 0.3, 0.8);
      const seatMaterial = new THREE.MeshPhongMaterial({ color: '#2c3e50' });
      const seat = new THREE.Mesh(seatGeometry, seatMaterial);
      seat.position.set(0, 0.8, 0);
      forkliftGroup.add(seat);
      
      // Mast
      const mastGeometry = new THREE.BoxGeometry(0.2, 3, 0.2);
      const mastMaterial = new THREE.MeshPhongMaterial({ color: '#95a5a6' });
      const mast = new THREE.Mesh(mastGeometry, mastMaterial);
      mast.position.set(0, 2.5, 0);
      forkliftGroup.add(mast);
      
      // Forks
      const forkGeometry = new THREE.BoxGeometry(0.1, 0.1, 1.5);
      const forkMaterial = new THREE.MeshPhongMaterial({ color: '#7f8c8d' });
      const leftFork = new THREE.Mesh(forkGeometry, forkMaterial);
      leftFork.position.set(-0.2, 1, 0.8);
      forkliftGroup.add(leftFork);
      
      const rightFork = new THREE.Mesh(forkGeometry, forkMaterial);
      rightFork.position.set(0.2, 1, 0.8);
      forkliftGroup.add(rightFork);
      
      // Wheels
      const wheelGeometry = new THREE.CylinderGeometry(0.3, 0.3, 0.2, 8);
      const wheelMaterial = new THREE.MeshPhongMaterial({ color: '#2c3e50' });
      
      const frontWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
      frontWheel.position.set(0, 0.3, 0.6);
      frontWheel.rotation.z = Math.PI / 2;
      forkliftGroup.add(frontWheel);
      
      const backWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
      backWheel.position.set(0, 0.3, -0.6);
      backWheel.rotation.z = Math.PI / 2;
      forkliftGroup.add(backWheel);
      
      forkliftGroup.position.set(x, 0, z);
      forkliftGroup.userData = {
        type: 'forklift',
        offset: Math.random() * Math.PI * 2,
        forks: [leftFork, rightFork]
      };
      
      return forkliftGroup;
    };
    
    // Add forklifts
    const forklifts = [
      createForklift(-30, -15, '#e74c3c'),
      createForklift(20, -35, '#f39c12'),
      createForklift(-15, 35, '#3498db')
    ];
    
    forklifts.forEach(forklift => {
      scene.add(forklift);
    });

    // Enhanced animation loop with human workers and vehicles
    const animate = () => {
      controls.update();
      
      const time = Date.now() * 0.001;
      
      // Animate human workers
      scene.children.forEach(child => {
        if (child.userData && child.userData.type === 'worker') {
          // Walking animation
          child.position.x += Math.sin(time * 2 + child.userData.offset) * 0.02;
          child.position.z += Math.cos(time * 1.5 + child.userData.offset) * 0.02;
          child.rotation.y = Math.sin(time * 2 + child.userData.offset) * 0.1;
          
          // Walking cycle animation
          if (child.userData.legs) {
            child.userData.legs.forEach((leg, index) => {
              leg.rotation.x = Math.sin(time * 4 + index * Math.PI) * 0.3;
            });
          }
          
          // Arm swinging animation
          if (child.userData.arms) {
            child.userData.arms.forEach((arm, index) => {
              arm.rotation.x = Math.sin(time * 4 + index * Math.PI + Math.PI) * 0.2;
            });
          }
        }
        
        // Animate lorries
        if (child.userData && child.userData.type === 'lorry') {
          child.position.x += child.userData.speed;
          if (child.position.x > 150) {
            child.position.x = -150;
          }
          
          // Wheel rotation
          if (child.userData.wheels) {
            child.userData.wheels.forEach(wheel => {
              wheel.rotation.z += child.userData.speed * 2;
            });
          }
        }
        
        // Animate forklifts
        if (child.userData && child.userData.type === 'forklift') {
          child.position.x += Math.sin(time * 0.5 + child.userData.offset) * 0.01;
          child.position.z += Math.cos(time * 0.3 + child.userData.offset) * 0.01;
          
          // Fork movement
          if (child.userData.forks) {
            child.userData.forks.forEach(fork => {
              fork.position.y = Math.sin(time * 2 + child.userData.offset) * 0.5 + 1;
            });
          }
        }
      });
      
      renderer.render(scene, camera);
      labelRenderer.render(scene, camera);
      requestAnimationFrame(animate);
    };
    animate();
    setSceneReady(true);

    // Cleanup
    return () => {
      if (mountRef.current && renderer.domElement.parentNode === mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
      if (labelRef.current && labelRenderer.domElement.parentNode === labelRef.current) {
        labelRef.current.removeChild(labelRenderer.domElement);
      }
      renderer.domElement.removeEventListener('click', onClick);
    };

    // If foundBin, move camera to it
    if (foundBin && controlsRef.current) {
      setTimeout(() => {
        controlsRef.current.object.position.set(
          foundBin.position.x,
          foundBin.position.y + 15,
          foundBin.position.z + 30
        );
        controlsRef.current.target.set(
          foundBin.position.x,
          foundBin.position.y,
          foundBin.position.z
        );
        controlsRef.current.update();
      }, 400);
    }
  }, [is3DView, statusFilters, foundBin, selectedWarehouse, warehouseDetails]);



  // Status filter handler
  const handleStatusFilter = status => {
    setStatusFilters(f => ({ ...f, [status]: !f[status] }));
  };

  // Debug function to test popup closing
  const testClosePopup = () => {
    console.log('Testing popup close, current popup:', popup);
    setPopup(null);
    console.log('Popup should be closed now');
  };

  // Warehouse selection handler
  const handleWarehouseChange = async (warehouseId) => {
    const warehouse = warehouses.find(w => w.id === warehouseId);
    setSelectedWarehouse(warehouse);
    
    if (warehouseId) {
      try {
        setDetailsLoading(true);
        const response = await warehouseService.getById(warehouseId);
        if (response && response.content) {
          setWarehouseDetails(response.content);
        }
      } catch (error) {
        console.error('Error loading warehouse details:', error);
        setWarehouseDetails(null);
      } finally {
        setDetailsLoading(false);
      }
    } else {
      setWarehouseDetails(null);
    }
  };

  // Status badge for popup
  const statusBadge = status => (
    <span style={{
      display: 'inline-block',
      minWidth: 60,
      background: STATUS_COLORS[status],
      color: status === 'verification' ? '#222' : '#fff',
      borderRadius: 6,
      padding: '2px 10px',
      fontWeight: 600,
      fontSize: 13,
      marginLeft: 8
    }}>{status.toUpperCase()}</span>
  );

  // Fullscreen handlers
  const handleFullscreenToggle = () => {
    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.mozRequestFullScreen) {
        containerRef.current.mozRequestFullScreen();
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    document.addEventListener('mozfullscreenchange', onFullscreenChange);
    document.addEventListener('MSFullscreenChange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
      document.removeEventListener('mozfullscreenchange', onFullscreenChange);
      document.removeEventListener('MSFullscreenChange', onFullscreenChange);
    };
  }, []);

  // Mock data for 2D view
  const mockSections = [
    {
      label: 'X01',
      subLabel: 'EP RM',
      bins: Array(20).fill().map((_, i) => ({ code: `A02-03-0${i+1}`, status: 'damage', qty: Math.floor(Math.random()*20+1) })),
    },
    {
      label: 'Z01',
      subLabel: 'EP RM',
      bins: Array(20).fill().map((_, i) => ({ code: `A02-03-0${i+1}`, status: 'damage', qty: Math.floor(Math.random()*20+1) })),
    },
    {
      label: 'A01',
      subLabel: 'YD RM',
      bins: Array(20).fill().map((_, i) => ({ code: `A02-03-0${i+1}`, status: i%2===0?'damage':'complete', qty: Math.floor(Math.random()*20+1) })),
    },
    {
      label: 'B01',
      subLabel: 'YD RM',
      bins: Array(20).fill().map((_, i) => ({ code: `A02-03-0${i+1}`, status: 'complete', qty: Math.floor(Math.random()*20+1) })),
    },
    {
      label: 'B02',
      subLabel: 'YD RM',
      bins: Array(20).fill().map((_, i) => ({ code: `A02-03-0${i+1}`, status: i%3===0?'verification':'complete', qty: Math.floor(Math.random()*20+1) })),
    },
  ];
  const statusColors = {
    damage: '#d32f2f',
    verification: '#ffeb3b',
    complete: '#388e3c',
    mt: '#90a4ae',
  };

  // Example transformation (customize as needed)
  const sections2D = React.useMemo(() => {
    if (!warehouseDetails || !warehouseDetails.racks) return [];
    return warehouseDetails.racks.map(rack => ({
      label: rack.name,
      subLabel: rack.type || '',
      bins: (rack.stockBins || []).map(bin => ({
        code: bin.code,
        status: getBinStatus(bin),
        qty: bin.availableStockQuantity,
      })),
    }));
  }, [warehouseDetails]);

  // Handler for rack dropdown
  function handleRackChange(rackId) {
    const rack = rackOptions.find(r => r.id === rackId);
    setSelectedRack(rack);
  }
  // Handler for bin dropdown
  function handleBinChange(binId) {
    const bin = binOptions.find(b => b.id === binId);
    setSelectedBin(bin);
  }

  // Add a handler for the search button
  function handleSearch() {
    const wmsId = selectedWarehouse?.id;
    if (!wmsId) return;
    let rackId, binId;
    if (selectedRack?.id) rackId = selectedRack.id;
    if (selectedBin?.id) binId = selectedBin.id;
    setWarehouseLoading(true);
    setWarehouseDetails(null); // Reset before fetching new data
    warehouseService.fetchWarehouseData(
      wmsId,
      rackId,
      binId
    )
      .then(data => {
        if (data && data.content && data.content.length > 0) {
          let warehouse = data.content[0];
          if (selectedRack?.id && warehouse.racks) {
            warehouse = {
              ...warehouse,
              racks: warehouse.racks.filter(r => r.id === selectedRack.id)
            };
          }
          setWarehouseDetails(warehouse);
          if (binId && selectedBin?.code) {
            centerOnBin(selectedBin.code);
          } else if (selectedRack?.id && selectedRack?.stockBins?.[0]?.code) {
            centerOnBin(selectedRack.stockBins[0].code);
          } else if (warehouse.racks?.[0]?.stockBins?.[0]?.code) {
            centerOnBin(warehouse.racks[0].stockBins[0].code);
          }
        }
      })
      .catch(() => setWarehouseDetails(null))
      .finally(() => setWarehouseLoading(false));
  }

  return (
    <div ref={containerRef} className="warehouse-3d-container" style={{ 
      width: '100%', 
      height: '800px', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'flex-start', 
      background: 'linear-gradient(135deg, #0f1419 0%, #1a1f2e 50%, #2d3748 100%)', 
      padding: 40, 
      position: 'relative', 
      overflow: 'hidden', 
      marginBottom: '32px',
      boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
      borderRadius: '20px'
    }}>
      {/* Animated background particles */}
      <div style={{ 
        position: 'absolute', 
        left: 0, 
        top: 0, 
        width: '100%', 
        height: '100%', 
        zIndex: 1, 
        background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%), radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.1) 0%, transparent 50%)',
        animation: 'pulse 4s ease-in-out infinite alternate'
      }} />
      
      {/* Vignette overlay */}
      <div style={{ 
        pointerEvents: 'none', 
        position: 'absolute', 
        left: 0, 
        top: 0, 
        width: '100%', 
        height: '100%', 
        zIndex: 2, 
        background: 'radial-gradient(ellipse at center, rgba(15,20,25,0) 60%, rgba(15,20,25,0.8) 100%)' 
      }} />


      
      {/* Ready Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        marginBottom: '16px'
      }}>
        <button 
          onClick={() => {
            if (selectedWarehouse) {
              centerOnBin(warehouseDetails?.racks?.[0]?.stockBins?.[0]?.code);
            }
          }}
          style={{
            background: 'linear-gradient(135deg, #4ade80, #22c55e)',
            color: '#fff',
            border: 'none',
            padding: '8px 24px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            transition: 'all 0.3s ease'
          }}
        >
          Ready
        </button>
      </div>

      <div className="warehouse-3d-card" style={{ 
        width: '100%', 
        maxWidth: '100%',
        background: 'linear-gradient(135deg, #2d3748 0%, #1a202c 100%)', 
        borderRadius: 24, 
        boxShadow: '0 20px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)', 
        padding: 0, 
        position: 'relative', 
        overflow: 'hidden', 
        zIndex: 3,
        border: '1px solid rgba(255,255,255,0.1)',
        transition: 'max-width 0.3s ease',
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          padding: '28px 36px 16px 36px', 
          borderBottom: '1px solid rgba(255,255,255,0.1)', 
          background: 'linear-gradient(135deg, rgba(45,55,72,0.8) 0%, rgba(26,31,46,0.8) 100%)', 
          zIndex: 3,
          backdropFilter: 'blur(10px)'
        }}>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Warehouse Selection */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label htmlFor="warehouse-select" style={{ 
                color: '#fff', 
                fontSize: 16, 
                fontWeight: 600,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}>
                📦 Warehouse:
              </label>
              {warehouseLoading ? (
                <div style={{ 
                  color: '#ffe066', 
                  fontSize: 14,
                  padding: '8px 16px',
                  background: 'rgba(255,224,102,0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,224,102,0.3)',
                  animation: 'pulse 2s infinite'
                }}>
                  ⏳ Loading warehouses...
                </div>
              ) : (
                <>
                <select
                  id="warehouse-select"
                  value={selectedWarehouse?.id || ''}
                  onChange={e => handleWarehouseChange(e.target.value)}
                  style={{ 
                    fontSize: 16, 
                    padding: '10px 16px', 
                    borderRadius: 12, 
                    background: 'linear-gradient(135deg, #fff 0%, #f7fafc 100%)', 
                    color: '#2d3748', 
                    border: '2px solid rgba(255,224,102,0.5)', 
                      minWidth: 220,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">Select a warehouse</option>
                  {warehouses.map(warehouse => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.name} ({warehouse.maxCapacities?.[0]?.volume || 0} {warehouse.maxCapacities?.[0]?.measurement?.name || 'CBM'})
                    </option>
                  ))}
                </select>
                  <select
                    id="rack-select"
                    value={selectedRack?.id || ''}
                    onChange={e => handleRackChange(e.target.value)}
                    style={{
                      fontSize: 16,
                      padding: '10px 16px',
                      borderRadius: 12,
                      background: 'linear-gradient(135deg, #fff 0%, #f7fafc 100%)',
                      color: '#2d3748',
                      border: '2px solid rgba(255,224,102,0.5)',
                      minWidth: 160,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      cursor: rackOptions.length === 0 ? 'not-allowed' : 'pointer',
                      opacity: rackOptions.length === 0 ? 0.5 : 1
                    }}
                    disabled={rackOptions.length === 0}
                  >
                    <option value="">Select a rack</option>
                    {rackOptions.map(rack => (
                      <option key={rack.id} value={rack.id}>{rack.name}</option>
                    ))}
                  </select>
                  <select
                    id="bin-select"
                    value={selectedBin?.id || ''}
                    onChange={e => handleBinChange(e.target.value)}
                    style={{
                      fontSize: 16,
                      padding: '10px 16px',
                      borderRadius: 12,
                      background: 'linear-gradient(135deg, #fff 0%, #f7fafc 100%)',
                      color: '#2d3748',
                      border: '2px solid rgba(255,224,102,0.5)',
                      minWidth: 120,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      cursor: binOptions.length === 0 ? 'not-allowed' : 'pointer',
                      opacity: binOptions.length === 0 ? 0.5 : 1
                    }}
                    disabled={binOptions.length === 0}
                  >
                    <option value="">Select a bin</option>
                    {binOptions.map(bin => (
                      <option key={bin.id} value={bin.id}>{bin.code}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleSearch}
                    style={{
                      fontSize: 16,
                      padding: '10px 24px',
                      borderRadius: 12,
                      background: 'linear-gradient(135deg, #60a5fa, #3b82f6)',
                      color: '#fff',
                      border: 'none',
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      transition: 'all 0.3s ease',
                      marginLeft: 8
                    }}
                    disabled={!selectedWarehouse}
                    title="Search and center view"
                  >
                    🔍 Search
                  </button>
                </>
              )}
            </div>
            <button
              onClick={() => setIs3DView(v => !v)}
              style={{
                marginLeft: 16,
                padding: '8px 18px',
                borderRadius: 8,
                border: 'none',
                background: is3DView ? 'linear-gradient(135deg, #60a5fa, #3b82f6)' : 'linear-gradient(135deg, #fbbf24, #f59e42)',
                color: '#fff',
                fontWeight: 700,
                  fontSize: 14, 
                  cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                transition: 'all 0.3s ease'
              }}
              title={is3DView ? 'Switch to 2D View' : 'Switch to 3D View'}
            >
              {is3DView ? '3D View' : '2D View'}
            </button>
          </div>
          <button
            onClick={handleFullscreenToggle}
            style={{
              marginLeft: 12,
              padding: '8px 18px',
              borderRadius: 8,
              border: 'none',
              background: isFullscreen ? 'linear-gradient(135deg, #f87171, #ef4444)' : 'linear-gradient(135deg, #10b981, #059669)',
              color: '#fff',
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
              gap: 8
            }}
            title={isFullscreen ? 'Exit Full Screen' : 'Full Screen'}
          >
            <span style={{fontSize:18}}>⛶</span> {isFullscreen ? 'Exit Full Screen' : 'Full Screen'}
          </button>
        </div>
        {/* 3D Canvas */}
        <div style={{ 
          width: '100%', 
          height: 600, 
          position: 'relative', 
          background: 'linear-gradient(135deg, #0f1419 0%, #1a1f2e 100%)',
          borderBottomLeftRadius: '24px',
          borderBottomRightRadius: '24px'
        }}>

          {warehouseLoading && (
            <div style={{ 
              position: 'absolute', 
              left: 0, 
              top: 0, 
              width: '100%', 
              height: '100%', 
              background: 'linear-gradient(135deg, rgba(15,20,25,0.95) 0%, rgba(26,31,46,0.95) 100%)', 
              zIndex: 10, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexDirection: 'column',
              gap: '20px',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ 
                color: '#ffe066', 
                fontSize: 28, 
                fontWeight: 'bold',
                textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                animation: 'pulse 2s infinite'
              }}>
                🏭 Loading Warehouses...
              </div>
              <div style={{ 
                color: '#fff', 
                fontSize: 16,
                opacity: 0.8,
                textAlign: 'center'
              }}>
                ⚡ Fetching warehouse data from API
              </div>
              <div style={{
                width: '60px',
                height: '60px',
                border: '4px solid rgba(255,224,102,0.3)',
                borderTop: '4px solid #ffe066',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            </div>
          )}
          {!warehouseLoading && !sceneReady && (
            <div style={{ 
              position: 'absolute', 
              left: 0, 
              top: 0, 
              width: '100%', 
              height: '100%', 
              background: 'linear-gradient(135deg, rgba(15,20,25,0.95) 0%, rgba(26,31,46,0.95) 100%)', 
              zIndex: 10, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexDirection: 'column',
              gap: '20px',
              backdropFilter: 'blur(10px)'
            }}
            >
              <div style={{ 
                color: '#60a5fa', 
                fontSize: 28, 
                fontWeight: 'bold',
                textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                animation: 'pulse 2s infinite'
              }}>
                🎮 Loading 3D View...
              </div>
              <div style={{
                width: '50px',
                height: '50px',
                border: '3px solid rgba(96,165,250,0.3)',
                borderTop: '3px solid #60a5fa',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            </div>
          )}
          {detailsLoading && (
            <div style={{ 
              position: 'absolute', 
              left: 0, 
              top: 0, 
              width: '100%', 
              height: '100%', 
              background: 'linear-gradient(135deg, rgba(15,20,25,0.95) 0%, rgba(26,31,46,0.95) 100%)', 
              zIndex: 10, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexDirection: 'column',
              gap: '20px',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ 
                color: '#4ade80', 
                fontSize: 28, 
                fontWeight: 'bold',
                textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                animation: 'pulse 2s infinite'
              }}>
                📊 Loading Warehouse Details...
              </div>
              <div style={{ 
                color: '#fff', 
                fontSize: 16,
                opacity: 0.8,
                textAlign: 'center'
              }}>
                🔍 Fetching racks and bins data
              </div>
              <div style={{
                width: '60px',
                height: '60px',
                border: '4px solid rgba(74,222,128,0.3)',
                borderTop: '4px solid #4ade80',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            </div>
          )}
          {is3DView ? (
          <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
          ) : (
            <div style={{
              width: '100%',
              minHeight: 600,
              background: '#10162a',
              padding: '32px 16px 16px 16px',
              borderRadius: 24,
              boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
              position: 'relative',
              overflowX: 'auto',
            }}>
              {/* Legend */}
              <div style={{ position: 'absolute', top: 16, right: 32, display: 'flex', gap: 24, zIndex: 2 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 16, height: 16, background: statusColors.damage, borderRadius: 4, display: 'inline-block' }} /> Damage</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 16, height: 16, background: statusColors.verification, borderRadius: 4, display: 'inline-block' }} /> Verification</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 16, height: 16, background: statusColors.complete, borderRadius: 4, display: 'inline-block' }} /> Complete</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 16, height: 16, background: statusColors.mt, borderRadius: 4, display: 'inline-block' }} /> MT</span>
              </div>
              {/* Sectioned grid from API */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 32, marginTop: 48 }}>
                {sections2D.length === 0 && (
                  <div style={{ color: '#fff', textAlign: 'center', fontSize: 24, marginTop: 80 }}>No data loaded</div>
                )}
                {sections2D.map((section, idx) => (
                  <React.Fragment key={section.label}>
                    {/* Dashed line before certain sections (optional, e.g. every 3 racks) */}
                    {idx > 0 && idx % 3 === 0 && (
                      <div style={{ width: '100%', borderTop: '3px dashed #ffe066', margin: '16px 0', position: 'relative', height: 0 }}>
                        <span style={{ position: 'absolute', left: 0, top: -18, color: '#ffe066', fontSize: 24 }}>»»</span>
                        <span style={{ position: 'absolute', right: 0, top: -18, color: '#ffe066', fontSize: 24 }}>»»</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                      {/* Section label */}
                      <div style={{ minWidth: 60, textAlign: 'center', color: '#fff', fontWeight: 700, fontSize: 18, letterSpacing: 1, marginTop: 8 }}>
                        {section.label}
                        <div style={{ fontWeight: 400, fontSize: 13, color: '#b0b8d1', marginTop: 2 }}>{section.subLabel}</div>
                      </div>
                      {/* Bins grid */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {/* Pathway row (optional, e.g. every 2 racks) */}
                        {idx > 0 && idx % 2 === 0 && (
                          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                            <div style={{ minWidth: 80, background: '#444a5a', color: '#fff', borderRadius: 6, padding: '8px 24px', fontWeight: 700, textAlign: 'center', letterSpacing: 1 }}>Pathway</div>
                            <div style={{ flex: 1 }} />
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 8 }}>
                          {section.bins.map((bin, i) => (
                            <div key={bin.code + i} style={{
                              minWidth: 60,
                              minHeight: 38,
                              background: statusColors[bin.status],
                              color: bin.status === 'verification' ? '#222' : '#fff',
                              borderRadius: 6,
                              fontWeight: 700,
                              fontSize: 13,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                              border: '2px solid #222',
                              letterSpacing: 1,
                              position: 'relative',
                            }}>
                              <span>{bin.code}</span>
                              <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8 }}>{bin.qty}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
          <div ref={labelRef} style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }} />
          
          {/* Center Button */}
            <button 
            onClick={() => {
              if (selectedWarehouse && warehouseDetails?.racks?.[0]?.stockBins?.[0]?.code) {
                centerOnBin(warehouseDetails.racks[0].stockBins[0].code);
              }
            }}
              style={{
                position: 'absolute',
                bottom: '20px',
                right: '20px',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
              background: 'linear-gradient(135deg, #60a5fa, #3b82f6)',
                border: 'none',
              color: '#fff',
                fontSize: '20px',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                zIndex: 100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
            title="Center on warehouse"
            >
              🎯
            </button>

          {/* Popup for bin info */}
          {popup && (
            <>
              {/* Backdrop overlay */}
              <div 
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'rgba(0, 0, 0, 0.5)',
                  zIndex: 199,
                  backdropFilter: 'blur(2px)'
                }}
                onClick={() => setPopup(null)}
              />
            <div 
              className="popup-content"
              onClick={(e) => e.stopPropagation()}
              style={{
                  position: 'fixed',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                background: 'linear-gradient(135deg, rgba(45,55,72,0.95) 0%, rgba(26,31,46,0.95) 100%)',
                color: '#fff',
                padding: '20px 28px 16px 22px',
                borderRadius: 16,
                zIndex: 200,
                boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,224,102,0.2)',
                minWidth: 320,
                maxWidth: 380,
                maxHeight: '80vh',
                overflowY: 'auto',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255,224,102,0.3)',
                animation: 'fadeInUp 0.3s ease-out'
              }}
            >
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Close button clicked');
                  setPopup(null);
                }} 
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                style={{ 
                position: 'absolute', 
                  top: 8, 
                  right: 8, 
                  background: 'rgba(220, 38, 38, 0.9)', 
                  border: '2px solid rgba(255,255,255,0.3)', 
                color: '#fff', 
                  fontSize: '20px', 
                  fontWeight: 'bold',
                cursor: 'pointer', 
                borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  zIndex: 202,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  userSelect: 'none',
                  ':hover': {
                    background: 'rgba(220, 38, 38, 1)',
                    transform: 'scale(1.1)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                  },
                  ':active': {
                    transform: 'scale(0.95)'
                  }
                }}
                title="Close popup"
                type="button"
              >✕</button>
              <div style={{ 
                fontWeight: 800, 
                fontSize: 18, 
                marginBottom: 12,
                marginTop: 8,
                background: 'linear-gradient(45deg, #ffe066, #ffd700)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                paddingRight: '40px'
              }}>
                📦 Bin: <span style={{ color: '#ffe066' }}>{popup.label}</span>
              </div>
              <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 16 }}>Status: {statusBadge(popup.status)}</div>
              
              {/* Container/Bin Code Information */}
              {popup.binData && (
                <div style={{ 
                  fontSize: 14, 
                  lineHeight: 1.6,
                  background: 'rgba(255,255,255,0.05)',
                  padding: '12px',
                  borderRadius: '8px',
                  marginTop: '8px',
                  marginBottom: '8px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: 8,
                    padding: '4px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <span style={{ color: '#90a4ae', fontWeight: 500 }}>🏷️ Bin Code:</span> 
                    <span style={{ color: '#ffe066', fontWeight: 600, fontFamily: 'monospace' }}>{popup.binData.code}</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: 8,
                    padding: '4px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <span style={{ color: '#90a4ae', fontWeight: 500 }}>📍 Location:</span> 
                    <span style={{ color: '#60a5fa', fontWeight: 600 }}>{popup.binData.location?.name || popup.binData.zone?.name || 'N/A'}</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: 8,
                    padding: '4px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <span style={{ color: '#90a4ae', fontWeight: 500 }}>📦 Container:</span> 
                    <span style={{ color: '#60a5fa', fontWeight: 600 }}>{popup.binData.container?.name || popup.binData.containerType?.name || 'N/A'}</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: 8,
                    padding: '4px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <span style={{ color: '#90a4ae', fontWeight: 500 }}>🏗️ Rack:</span> 
                    <span style={{ color: '#4ade80', fontWeight: 600 }}>{popup.binData.rack?.name || popup.binData.rackCode || 'N/A'}</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: 4,
                    padding: '4px 0'
                  }}>
                    <span style={{ color: '#90a4ae', fontWeight: 500 }}>🏢 Warehouse:</span> 
                    <span style={{ color: '#fbbf24', fontWeight: 600 }}>{selectedWarehouse?.name || 'N/A'}</span>
                  </div>
                </div>
              )}
              
              {popup.binData && (
                <div style={{ 
                  fontSize: 14, 
                  lineHeight: 1.6,
                  background: 'rgba(255,255,255,0.05)',
                  padding: '12px',
                  borderRadius: '8px',
                  marginTop: '8px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: 8,
                    padding: '4px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <span style={{ color: '#90a4ae', fontWeight: 500 }}>📦 Available:</span> 
                    <span style={{ color: '#4ade80', fontWeight: 600 }}>{popup.binData.availableStockQuantity}</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: 8,
                    padding: '4px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <span style={{ color: '#90a4ae', fontWeight: 500 }}>✅ Allowed:</span> 
                    <span style={{ color: '#60a5fa', fontWeight: 600 }}>{popup.binData.allowedStockQuantity}</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: 8,
                    padding: '4px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <span style={{ color: '#90a4ae', fontWeight: 500 }}>📊 Total:</span> 
                    <span style={{ color: '#fbbf24', fontWeight: 600 }}>{popup.binData.totalStocks}</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: 8,
                    padding: '4px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <span style={{ color: '#90a4ae', fontWeight: 500 }}>🎨 Color:</span> 
                    <span style={{ color: '#fff', fontWeight: 600 }}>{popup.binData.color}</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: 8,
                    padding: '4px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <span style={{ color: '#90a4ae', fontWeight: 500 }}>🏪 Store:</span> 
                    <span style={{ color: '#fff', fontWeight: 600 }}>{popup.binData.store?.name}</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: 8,
                    padding: '4px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <span style={{ color: '#90a4ae', fontWeight: 500 }}>📋 Material:</span> 
                    <span style={{ color: '#fbbf24', fontWeight: 600 }}>{popup.binData.material?.name || 'N/A'}</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: 4,
                    padding: '4px 0'
                  }}>
                    <span style={{ color: '#90a4ae', fontWeight: 500 }}>📏 Unit:</span> 
                    <span style={{ color: '#a78bfa', fontWeight: 600 }}>{popup.binData.unit?.name || 'N/A'}</span>
                  </div>
                </div>
              )}
              
              <div style={{ 
                fontSize: 12, 
                opacity: 0.6, 
                marginTop: 16,
                textAlign: 'center',
                fontStyle: 'italic',
                padding: '8px 12px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                💡 Click × or press ESC to close • Scroll for more details
              </div>
            </div>
            </>
          )}
        </div>
        {/* Legend Bar */}
        <div className="warehouse-3d-legend" style={{ 
          width: '100%', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: 24, 
          background: 'linear-gradient(135deg, rgba(45,55,72,0.95) 0%, rgba(26,31,46,0.95) 100%)', 
          padding: '16px 0', 
          borderTop: '1px solid rgba(255,255,255,0.1)', 
          fontSize: 14, 
          color: '#fff', 
          fontWeight: 600, 
          letterSpacing: 1,
          backdropFilter: 'blur(10px)',
          borderBottomLeftRadius: '24px',
          borderBottomRightRadius: '24px',
          zIndex: 15,
          position: 'relative',
          marginLeft: showPanels && showStatistics ? '550px' : '0',
          transition: 'margin-left 0.3s ease',
          border: '2px solid rgba(255,224,102,0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', background: 'rgba(211,47,47,0.1)', borderRadius: '8px', border: '1px solid rgba(211,47,47,0.3)' }}>
            <span style={{ display: 'inline-block', width: 16, height: 16, background: '#d32f2f', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }} /> 
            High Stock (≥80%)
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', background: 'rgba(255,235,59,0.1)', borderRadius: '8px', border: '1px solid rgba(255,235,59,0.3)' }}>
            <span style={{ display: 'inline-block', width: 16, height: 16, background: '#ffeb3b', borderRadius: '4px', border: '1px solid #888', boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }} /> 
            Medium Stock (≥50%)
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', background: 'rgba(56,142,60,0.1)', borderRadius: '8px', border: '1px solid rgba(56,142,60,0.3)' }}>
            <span style={{ display: 'inline-block', width: 16, height: 16, background: '#388e3c', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }} /> 
            Low Stock (under 50%)
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', background: 'rgba(144,164,174,0.1)', borderRadius: '8px', border: '1px solid rgba(144,164,174,0.3)' }}>
            <span style={{ display: 'inline-block', width: 16, height: 16, background: '#90a4ae', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }} /> 
            Empty
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', background: 'rgba(255,224,102,0.1)', borderRadius: '8px', border: '1px solid rgba(255,224,102,0.3)' }}>
            <span style={{ display: 'inline-block', width: 16, height: 16, background: 'rgba(255,255,255,0.3)', borderRadius: '4px', border: '2px solid #ffe066', boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }} /> 
            Has Stock (Glow)
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', background: 'rgba(74,222,128,0.1)', borderRadius: '8px', border: '1px solid rgba(74,222,128,0.3)' }}>
            <span style={{ fontSize: '12px', color: '#4ade80' }}>📊</span> 
            Footer Active
        </div>
      </div>
          </div>
          


      {/* Status Indicator */}
      <div style={{ 
        position: 'absolute', 
        top: 32, 
        left: '50%', 
        transform: 'translateX(-50%)', 
        background: 'rgba(35,38,58,0.95)', 
        padding: '8px 16px', 
        borderRadius: '20px', 
        border: '2px solid #444',
        color: '#fff',
        zIndex: 10,
        fontSize: '14px'
      }}>
        {warehouseLoading ? 'Loading Warehouses...' : 
         detailsLoading ? 'Loading Details...' : 
         warehouseDetails ? `Ready (Info: ${showWarehouseInfo ? 'ON' : 'OFF'}, Stats: ${showStatistics ? 'ON' : 'OFF'})` : 'Select Warehouse'}
      </div>




    </div>
  );
};

export default Store3DView; 